import { useContext } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminContext } from "./context/AdminContext";
import Layout from "./components/Layout";
import Dashboard from "./pages/Administrator/Dashboard";
import AddCareGiver from "./pages/Administrator/caregiver/addcaregiver/AddCareGiver";
import CareGiverList from "./pages/Administrator/caregiver/CareGiverList";
import Clients from "./pages/Administrator/client/Clients";
import Vetting from "./pages/Administrator/vetting/Vetting";
import VerifiedCareGivers from "./pages/Administrator/caregiver/VerifiedCareGivers";
import RequestPostingForm from "./pages/Administrator/servicerequest/RequestPostingForm";
import RequestManagementTable from "./pages/Administrator/servicerequest/RequestManagementTable";
import ClientDetails from "./pages/Administrator/client/ClientDetails";
import AddClient from "./pages/Administrator/client/AddClient";
import SubAdminList from "./pages/Administrator/admins/SubAdminList";
import AddSubAdmin from "./pages/Administrator/admins/AddSubAdmin";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLogin from "./pages/AdminLogin";
import MatchingConfiguration from "./pages/Administrator/MatchingConfiguration";
import NotFound from "./pages/NotFound";
import AssignCaregiver from "./pages/Administrator/servicerequest/AssignCaregiver";
import TrainingList from "./pages/Administrator/skilldev/TrainingList";
import BookPurchaseList from "./pages/Administrator/skilldev/BookPurchaseList";
import TrainingAndBookDashboard from "./pages/Administrator/skilldev/TrainingAndBookDashboard";
import ContactUs from "./pages/Administrator/engagment/ContactUs";
import Newsletter from "./pages/Administrator/engagment/Newsletter";
import ReplaceCaregiver from "./pages/Administrator/booking/ReplaceCaregiver";
import CategoryList from "./pages/Administrator/categories/CategoryList";
import CreateCategory from "./pages/Administrator/categories/CreateCategory";
import EditCategory from "./pages/Administrator/categories/EditCategory";
import Coverage from "./pages/Administrator/engagment/Coverage";
import Tasks from "./pages/Administrator/Tasks";
import InvoiceDashboard from "./pages/Administrator/invoices/InvoiceDashboard";
import NewInvoice from "./pages/Administrator/invoices/NewInvoice";
import InvoicesList from "./pages/Administrator/invoices/InvoicesList";
import Agreements from "./pages/Administrator/agreement/Agreement";
import AgreementTemplateDetails from "./pages/Administrator/agreement/AgreementTemplateDetails";
import AgreementBuilder from "./pages/Administrator/agreement/AgreementBuilder";
import Settings from "./pages/Administrator/guide/Settings";
import Help from "./pages/Administrator/guide/Help";
import Article from "./pages/Administrator/guide/Article";
import Bookings from "./pages/Administrator/booking/Bookings";
import JobApplicants from "./pages/Administrator/servicerequest/JobApplicants";
import {
  BlogList,
  BlogForm,
  BlogPreview,
  BlogAnalytics,
  CategoryManager,
  ActivityLogs,
} from "./pages/Administrator/parenthub";
import ContractList from "./pages/Administrator/contracts/ContractList";
import ContractDetail from "./pages/Administrator/contracts/ContractDetail";
import PayoutDashboard from "./pages/Administrator/payouts/PayoutDashboard";
import PayoutList from "./pages/Administrator/payouts/PayoutList";
import ReviewList from "./pages/Administrator/reviews/ReviewList";
import { ADMIN_ACCESS } from "./utils/adminAccess";

const App = () => {
  const { aToken } = useContext(AdminContext);
  const withAccess = (requiredAccess, element) => (
    <ProtectedRoute requiredAccess={requiredAccess}>{element}</ProtectedRoute>
  );

  return (
    <div className="bg-[#F8F9FD]">
      <ToastContainer />
      {aToken ? (
        <Layout>
          <Routes>
            <Route
              path="/"
              element={<Navigate to="/admins/dashboard" />}
            />

            {/* Admin Routes */}
            <Route
              path="/admins/dashboard"
              element={withAccess(ADMIN_ACCESS.DASHBOARD, <Dashboard />)}
            />
            <Route
              path="/bookings"
              element={withAccess(ADMIN_ACCESS.BOOKINGS, <Bookings />)}
            />
            <Route
              path="/bookings/:id/replace"
              element={withAccess(
                ADMIN_ACCESS.BOOKINGS,
                <ReplaceCaregiver />,
              )}
            />
            <Route
              path="/caregivers/add"
              element={withAccess(ADMIN_ACCESS.CAREGIVERS, <AddCareGiver />)}
            />
            <Route
              path="/caregivers/all"
              element={withAccess(ADMIN_ACCESS.CAREGIVERS, <CareGiverList />)}
            />
            <Route
              path="/caregivers/edit/:id"
              element={withAccess(ADMIN_ACCESS.CAREGIVERS, <AddCareGiver />)}
            />
            <Route
              path="/verified/caregivers"
              element={withAccess(
                ADMIN_ACCESS.CAREGIVERS,
                <VerifiedCareGivers />,
              )}
            />
            <Route
              path="/vetting"
              element={withAccess(ADMIN_ACCESS.VETTING, <Vetting />)}
            />
            <Route
              path="/clients"
              element={withAccess(ADMIN_ACCESS.CLIENTS, <Clients />)}
            />
            <Route
              path="/clients/add"
              element={withAccess(ADMIN_ACCESS.CLIENTS, <AddClient />)}
            />
            <Route
              path="/clients/:userId"
              element={withAccess(ADMIN_ACCESS.CLIENTS, <ClientDetails />)}
            />

            <Route
              path="/requests/post"
              element={withAccess(
                ADMIN_ACCESS.SERVICE_REQUESTS,
                <RequestPostingForm />,
              )}
            />
            <Route
              path="/requests/manage"
              element={withAccess(
                ADMIN_ACCESS.SERVICE_REQUESTS,
                <RequestManagementTable />,
              )}
            />
            <Route
              path="/requests/:jobId/applicants"
              element={withAccess(
                ADMIN_ACCESS.SERVICE_REQUESTS,
                <JobApplicants />,
              )}
            />
            <Route
              path="/requests/:jobId/assign"
              element={withAccess(
                ADMIN_ACCESS.SERVICE_REQUESTS,
                <AssignCaregiver />,
              )}
            />

            <Route
              path="/invoices/new"
              element={withAccess(ADMIN_ACCESS.INVOICES, <NewInvoice />)}
            />
            <Route
              path="/invoices/lists"
              element={withAccess(ADMIN_ACCESS.INVOICES, <InvoicesList />)}
            />
            <Route
              path="/invoices/dashboard"
              element={withAccess(
                ADMIN_ACCESS.INVOICES,
                <InvoiceDashboard />,
              )}
            />
            <Route
              path="/matching/configuration"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <MatchingConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admins/list"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <SubAdminList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admins/add"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <AddSubAdmin />
                </ProtectedRoute>
              }
            />

            <Route
              path="/btp/training-requests"
              element={withAccess(
                ADMIN_ACCESS.BOOK_TRAINING,
                <TrainingList />,
              )}
            />
            <Route
              path="/btp/book-purchases"
              element={withAccess(
                ADMIN_ACCESS.BOOK_TRAINING,
                <BookPurchaseList />,
              )}
            />
            <Route
              path="/btp/dashboard"
              element={withAccess(
                ADMIN_ACCESS.BOOK_TRAINING,
                <TrainingAndBookDashboard />,
              )}
            />
            <Route
              path="/engage/coverage"
              element={withAccess(ADMIN_ACCESS.ENGAGEMENT, <Coverage />)}
            />
            <Route
              path="/engage/contact-us"
              element={withAccess(ADMIN_ACCESS.ENGAGEMENT, <ContactUs />)}
            />
            <Route
              path="/engage/newsletter"
              element={withAccess(ADMIN_ACCESS.ENGAGEMENT, <Newsletter />)}
            />

            {/* Service Categories */}
            <Route
              path="/categories"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <CategoryList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/create"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <CreateCategory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/:id/edit"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <EditCategory />
                </ProtectedRoute>
              }
            />

            {/* Contracts & Payouts */}
            <Route
              path="/contracts"
              element={withAccess(ADMIN_ACCESS.CONTRACTS, <ContractList />)}
            />
            <Route
              path="/contracts/:contractId"
              element={withAccess(ADMIN_ACCESS.CONTRACTS, <ContractDetail />)}
            />
            <Route
              path="/payouts"
              element={withAccess(ADMIN_ACCESS.PAYOUTS, <PayoutDashboard />)}
            />
            <Route
              path="/payouts/list"
              element={withAccess(ADMIN_ACCESS.PAYOUTS, <PayoutList />)}
            />

            {/* Reviews */}
            <Route
              path="/reviews"
              element={withAccess(ADMIN_ACCESS.REVIEWS, <ReviewList />)}
            />

            <Route
              path="/tasks"
              element={
                <ProtectedRoute requiredRole="superAdmin">
                  <Tasks />
                </ProtectedRoute>
              }
            />

            {/* Parent Hub */}
            <Route
              path="/parent-hub/blogs"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <BlogList />)}
            />
            <Route
              path="/parent-hub/blogs/new"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <BlogForm />)}
            />
            <Route
              path="/parent-hub/blogs/edit/:blogId"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <BlogForm />)}
            />
            <Route
              path="/parent-hub/blogs/preview/:blogId"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <BlogPreview />)}
            />
            <Route
              path="/parent-hub/analytics"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <BlogAnalytics />)}
            />
            <Route
              path="/parent-hub/categories"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <CategoryManager />)}
            />
            <Route
              path="/parent-hub/activity-logs"
              element={withAccess(ADMIN_ACCESS.PARENT_HUB, <ActivityLogs />)}
            />

            <Route
              path="/agreements"
              element={withAccess(ADMIN_ACCESS.CONTRACTS, <Agreements />)}
            />
            <Route
              path="/agreements/create"
              element={withAccess(
                ADMIN_ACCESS.CONTRACTS,
                <AgreementBuilder />,
              )}
            />
            <Route
              path="/agreements/edit/:id"
              element={withAccess(
                ADMIN_ACCESS.CONTRACTS,
                <AgreementBuilder />,
              )}
            />
            <Route
              path="/agreements/templates/:id"
              element={withAccess(
                ADMIN_ACCESS.CONTRACTS,
                <AgreementTemplateDetails />,
              )}
            />
            <Route
              path="/admins/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route path="/help" element={<Help />} />
            <Route path="/help/article/:id" element={<Article />} />

            {/* 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      ) : (
        // Login Pages
        <Routes>
          <Route path="/" element={<AdminLogin />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      )}
    </div>
  );
};

export default App;
